import { useState } from 'react';
import { Box, Collapse, Drawer, IconButton } from '@mui/material';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import NorthEastOutlined from '@mui/icons-material/NorthEastOutlined';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acquireApiToken } from '../../api/client';
import { useToast } from '../../components';
import {
  SA_NAV,
  isSaExternal,
  isSaGroup,
  resolveSaLeaf,
  type SaGroupItem,
  type SaLeaf,
} from './saNav';

export const SA_SIDEBAR_WIDTH = 256;

/** A routed nav row (top-level leaf or a group child at `depth=1`). */
function SaLeafRow({
  leaf,
  depth = 0,
  onNavigate,
}: {
  leaf: SaLeaf;
  depth?: number;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const Icon = leaf.icon;
  return (
    <Box
      component={NavLink}
      to={leaf.to}
      end={leaf.to === '/site'}
      onClick={onNavigate}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minHeight: depth ? 36 : 40,
        pl: depth ? '44px' : '16px',
        pr: 2,
        py: depth ? '7px' : '9px',
        mb: '1px',
        position: 'relative',
        textDecoration: 'none',
        color: depth ? 'brand.sidebarFgMuted' : 'brand.sidebarFg',
        fontFamily: (theme) => theme.typography.button.fontFamily,
        fontSize: depth ? 13 : 13.5,
        lineHeight: 1.2,
        fontWeight: depth ? 450 : 500,
        transition: 'background 120ms ease',
        '& .sa-nav-icon': { color: 'brand.sidebarFgMuted' },
        '&:hover': { bgcolor: 'brand.sidebarHoverBg' },
        '&.active': {
          bgcolor: 'brand.sidebarActiveBg',
          color: 'brand.sidebarActiveFg',
          fontWeight: 600,
          '& .sa-nav-icon': { color: 'brand.sidebarActiveFg' },
        },
        // Child rows carry a hairline indent guide instead of a leading icon.
        ...(depth
          ? {
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 26,
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: 'brand.sidebarDivider',
              },
            }
          : {}),
      }}
    >
      {depth === 0 && <Icon className="sa-nav-icon" sx={{ fontSize: 17 }} />}
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {t(leaf.labelKey)}
      </Box>
    </Box>
  );
}

/** An expandable group header (Catalog / Ingestion / …). */
function SaGroupRow({
  group,
  open,
  active,
  onToggle,
}: {
  group: SaGroupItem;
  open: boolean;
  active: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const Icon = group.icon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        minHeight: 40,
        px: '16px',
        py: '9px',
        mb: '1px',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        bgcolor: active && !open ? 'brand.sidebarActiveBg' : 'transparent',
        color: active && !open ? 'brand.sidebarActiveFg' : 'brand.sidebarFg',
        fontFamily: (theme) => theme.typography.button.fontFamily,
        fontSize: 13.5,
        lineHeight: 1.2,
        fontWeight: active ? 600 : 500,
        transition: 'background 120ms ease',
        '& .sa-nav-icon': {
          color: active && !open ? 'brand.sidebarActiveFg' : 'brand.sidebarFgMuted',
        },
        '&:hover': { bgcolor: 'brand.sidebarHoverBg' },
      }}
    >
      <Icon className="sa-nav-icon" sx={{ fontSize: 17 }} />
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {t(group.labelKey)}
      </Box>
      {open ? (
        <ExpandMore sx={{ fontSize: 16, color: 'brand.sidebarFgMuted' }} />
      ) : (
        <ChevronRight sx={{ fontSize: 16, color: 'brand.sidebarFgMuted' }} />
      )}
    </Box>
  );
}

function SaSidebarContent({
  isMobile,
  onMobileClose,
}: {
  isMobile: boolean;
  onMobileClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const active = resolveSaLeaf(location.pathname);
  const activeParentId = active?.parent?.id ?? null;
  const [expanded, setExpanded] = useState<string[]>(() =>
    activeParentId ? [activeParentId] : [],
  );
  const onNavigate = isMobile ? onMobileClose : undefined;

  const isOpen = (id: string) => expanded.includes(id) || id === activeParentId;
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const openNow = prev.includes(id) || id === activeParentId;
      return openNow ? prev.filter((x) => x !== id && x !== activeParentId) : [...prev, id];
    });

  // The Hangfire dashboard is served by the API and authenticates bearer tokens,
  // so no plain link can reach it — a browser navigation carries no Authorization
  // header. Hand the token to the API's bridge endpoint instead; it stores the
  // token in a path-scoped cookie and redirects to the dashboard (BUG-012).
  const openApiTab = async (apiPath: string) => {
    // Opened synchronously, still inside the click: a window.open that runs after
    // an await has left the user-gesture context and browsers block it as a popup.
    // Deliberately without `noopener`, which would null the handle needed below —
    // the destination is our own API origin.
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error(
        t('siteAdmin:nav.jobsErrors.popupBlocked'),
        t('siteAdmin:nav.jobsErrors.popupBlockedHint'),
      );
      return;
    }

    const token = await acquireApiToken();
    if (!token) {
      tab.close();
      toast.error(t('siteAdmin:nav.jobsErrors.noToken'), t('siteAdmin:nav.jobsErrors.noTokenHint'));
      return;
    }

    tab.location.href = `${import.meta.env.VITE_API_BASE_URL}${apiPath}?token=${encodeURIComponent(token)}`;
  };

  const env =
    import.meta.env.MODE === 'production'
      ? t('siteAdmin:shell.env.production')
      : t('siteAdmin:shell.env.development');
  const build = import.meta.env.MODE;

  return (
    <Box
      sx={{
        width: SA_SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'brand.sidebarBg',
        color: 'brand.sidebarFg',
        borderRight: '1px solid',
        borderColor: 'brand.sidebarBorder',
      }}
    >
      {/* wordmark + subtitle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: '18px',
          py: '16px',
          borderBottom: '1px solid',
          borderColor: 'brand.sidebarDivider',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(140deg, #1A5991 0%, #004785 100%)',
            boxShadow: '0 2px 8px rgba(0,71,133,0.45)',
          }}
        >
          <ShieldOutlined sx={{ fontSize: 17, color: '#fff' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 700,
              fontSize: 15.5,
              lineHeight: 1.1,
              color: '#fff',
              letterSpacing: '-0.01em',
            }}
          >
            {t('siteAdmin:shell.wordmark')}
          </Box>
          <Box
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 600,
              fontSize: 9.5,
              lineHeight: 1.2,
              mt: '3px',
              color: '#8FB8E8',
              textTransform: 'uppercase',
              letterSpacing: '1.1px',
            }}
          >
            {t('siteAdmin:shell.wordmarkSubtitle')}
          </Box>
        </Box>
        {isMobile && (
          <IconButton
            onClick={onMobileClose}
            aria-label={t('actions.close')}
            sx={{ ml: 'auto', color: 'brand.sidebarFg' }}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* nav */}
      <Box
        component="nav"
        aria-label={t('siteAdmin:shell.wordmarkSubtitle')}
        sx={{ flex: 1, overflowY: 'auto', py: '8px' }}
      >
        {SA_NAV.map((section, i) => (
          <Box key={section.groupKey ?? `top-${i}`} sx={{ mt: section.groupKey ? '12px' : '4px' }}>
            {section.groupKey && (
              <Box
                sx={{
                  px: '18px',
                  pt: '4px',
                  pb: '6px',
                  fontFamily: (theme) => theme.typography.button.fontFamily,
                  fontWeight: 700,
                  fontSize: 10,
                  lineHeight: 1,
                  color: 'rgba(255,255,255,0.38)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {t(section.groupKey)}
              </Box>
            )}
            {section.items.map((item) => {
              if (isSaExternal(item)) {
                const Icon = item.icon;
                return (
                  <Box
                    key={item.id}
                    component="button"
                    type="button"
                    onClick={() => void openApiTab(item.apiPath)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      width: '100%',
                      minHeight: 40,
                      px: '16px',
                      py: '9px',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      bgcolor: 'transparent',
                      color: 'brand.sidebarFg',
                      fontFamily: (theme) => theme.typography.button.fontFamily,
                      fontSize: 13.5,
                      fontWeight: 500,
                      '& .sa-nav-icon': { color: 'brand.sidebarFgMuted' },
                      '&:hover': { bgcolor: 'brand.sidebarHoverBg' },
                    }}
                  >
                    <Icon className="sa-nav-icon" sx={{ fontSize: 17 }} />
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      {t(item.labelKey)}
                    </Box>
                    <NorthEastOutlined sx={{ fontSize: 14, color: 'brand.sidebarFgMuted' }} />
                  </Box>
                );
              }
              if (isSaGroup(item)) {
                const open = isOpen(item.id);
                return (
                  <Box key={item.id}>
                    <SaGroupRow
                      group={item}
                      open={open}
                      active={activeParentId === item.id}
                      onToggle={() => toggle(item.id)}
                    />
                    <Collapse in={open} unmountOnExit>
                      <Box sx={{ py: '2px' }}>
                        {item.children.map((child) => (
                          <SaLeafRow
                            key={child.id}
                            leaf={child}
                            depth={1}
                            onNavigate={onNavigate}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              }
              return <SaLeafRow key={item.id} leaf={item} onNavigate={onNavigate} />;
            })}
          </Box>
        ))}
      </Box>

      {/* environment footer — reinforces "internal console" */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'brand.sidebarDivider',
          px: '16px',
          py: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#22C55E',
            flexShrink: 0,
            boxShadow: '0 0 0 3px rgba(34,197,94,0.18)',
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 600,
              fontSize: 11.5,
              lineHeight: 1.2,
              color: 'rgba(255,255,255,0.82)',
            }}
          >
            {env}
          </Box>
          <Box
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 400,
              fontSize: 10,
              lineHeight: 1.2,
              mt: '2px',
              color: 'rgba(255,255,255,0.40)',
            }}
          >
            {t('siteAdmin:shell.env.build', { build })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface SaSidebarProps {
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/** The Site Admin console rail — permanent on desktop, a temporary drawer on mobile. */
export function SaSidebar({ isMobile, mobileOpen, onMobileClose }: SaSidebarProps) {
  const content = <SaSidebarContent isMobile={isMobile} onMobileClose={onMobileClose} />;
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(4, 29, 51, 0.45)' } } }}
        sx={{ '& .MuiDrawer-paper': { width: SA_SIDEBAR_WIDTH, border: 0 } }}
      >
        {content}
      </Drawer>
    );
  }
  return (
    <Box component="aside" sx={{ width: SA_SIDEBAR_WIDTH, flexShrink: 0 }}>
      {content}
    </Box>
  );
}
